import { useRef, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Play, Pause, X, Volume2 } from "lucide-react";
import { formatTime } from "./types";

interface AudioPlayerProps {
  audioUrl: string;
  playingClientId: number | null;
  testId: string | null;
  onClose: () => void;
}

export const AudioPlayer = ({
  audioUrl,
  playingClientId,
  testId,
  onClose,
}: AudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    // Reset state when audio URL changes
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  }, [audioUrl]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (audioRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * duration;
    }
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    onClose();
  };

  return (
    <div className="border-t bg-background px-4 py-3">
      <div className="flex items-center gap-4 max-w-full">
        <div className="shrink-0 min-w-[140px] max-w-[180px] max-md:hidden">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-muted flex items-center justify-center">
              <Volume2 className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium text-sm text-foreground">
                Client {playingClientId}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                {testId && `Test ${testId.substring(0, 8)}...`}
              </span>
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center gap-3 min-w-0">
          <Button
            size="icon"
            className="w-9 h-9 rounded-full"
            onClick={handlePlayPause}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" />
            ) : (
              <Play className="h-4 w-4 ml-0.5" />
            )}
          </Button>

          <div className="flex-1 flex items-center gap-3 min-w-0">
            <audio
              ref={audioRef}
              autoPlay
              src={audioUrl}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              className="hidden"
            />
            <span className="text-xs font-medium text-muted-foreground tabular-nums min-w-[40px] text-center">
              {formatTime(currentTime)}
            </span>
            <div
              className="flex-1 h-2 bg-muted rounded-full cursor-pointer relative overflow-hidden"
              onClick={handleSeek}
            >
              <div
                className="h-full bg-primary rounded-full transition-[width] duration-100"
                style={{
                  width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                }}
              />
            </div>
            <span className="text-xs font-medium text-muted-foreground tabular-nums min-w-[40px] text-center">
              {formatTime(duration)}
            </span>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-muted"
          onClick={handleClose}
          title="Close player"
        >
          <X className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};
